/**
 * Subaccounts — localStorage-backed keypair storage and public views.
 *
 * Sub keypairs live in localStorage keyed by chainId+master. The private keys are TRADE/FUND
 * scoped to the master relationship — a sub can only send its balance back to its own master.
 */
import { ethers } from "ethers";
import { LocalStorageKeyEnum } from "../enums/localStorage";
import { StoredSubaccount, SubaccountView, SubMap } from "./subaccounts.types";

const keyFor = (master: string, chainId: number) => `${chainId}:${master.toLowerCase()}`;

function loadAll(): SubMap {
  try {
    const raw = localStorage.getItem(LocalStorageKeyEnum.SUBACCOUNTS);
    return raw ? (JSON.parse(raw) as SubMap) : {};
  } catch {
    return {};
  }
}

function saveAll(map: SubMap): void {
  try {
    localStorage.setItem(LocalStorageKeyEnum.SUBACCOUNTS, JSON.stringify(map));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export function loadFor(master: string, chainId: number): StoredSubaccount[] {
  return loadAll()[keyFor(master, chainId)] || [];
}

export function saveFor(master: string, chainId: number, list: StoredSubaccount[]): void {
  const map = loadAll();
  map[keyFor(master, chainId)] = list;
  saveAll(map);
}

/** Public list of a master's subaccounts on a chain (private keys stripped). */
export function listSubaccounts(
  master: string | undefined | null,
  chainId: number | undefined | null
): SubaccountView[] {
  if (!master || !chainId) return [];
  return loadFor(master, chainId).map((s) => ({
    address: s.address,
    label: s.label,
    createdAt: s.createdAt,
    linked: s.linked,
  }));
}

/** The app-held wallet for a subaccount, used to sign its own consent / defund transfers. */
export function getSubaccountWallet(
  master: string | undefined | null,
  chainId: number | undefined | null,
  subAddress: string
): ethers.Wallet | null {
  if (!master || !chainId) return null;
  const stored = loadFor(master, chainId).find(
    (s) => s.address.toLowerCase() === subAddress.toLowerCase()
  );
  if (!stored) return null;
  try {
    return new ethers.Wallet(stored.privateKey);
  } catch {
    return null;
  }
}

/** Remove a subaccount from local storage (after unlink, or to forget a stale entry). */
export function removeSubaccountLocal(master: string, chainId: number, subAddress: string): void {
  const list = loadFor(master, chainId).filter(
    (s) => s.address.toLowerCase() !== subAddress.toLowerCase()
  );
  saveFor(master, chainId, list);
}
